import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { canAccessAgencyAdmin } from '../../utils/roleUtils';
import AdminPageHeader from '../../components/admin/AdminPageHeader';
import AdminStatCard from '../../components/admin/AdminStatCard';
import AdminPanel from '../../components/admin/AdminPanel';
import AdminEmptyState from '../../components/admin/AdminEmptyState';
import agencyAdminService from '../../services/agencyAdminService';
import { 
  Users, 
  Calendar, 
  FileText, 
  AlertCircle, 
  CheckCircle,
  Clock, 
  RefreshCw, 
  ArrowRight,
  AlertTriangle
} from 'lucide-react';
import '../../styles/admin-theme.css';

const AgencyDashboard = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (user && !canAccessAgencyAdmin(user.role)) {
      navigate('/agent/dashboard');
      return;
    }
    if (user) {
      fetchDashboardSummary();
    }
  }, [user, navigate]);

  // Check if user has agency admin access
  const hasAgencyAdminAccess = user && canAccessAgencyAdmin(user.role);

  const fetchDashboardSummary = async (refresh = false) => {
    try {
      if (refresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await agencyAdminService.getDashboardSummary();
      
      // Handle response structure (could be { data: {...} } or direct object)
      const data = response.data || response;
      setSummary(data);

    } catch (err) {
      console.error('Error fetching admin dashboard summary:', err);
      
      // Handle 404 - show user-friendly message instead of "not implemented"
      if (err.status === 404) {
        setError("We're preparing your agency metrics. You can still manage agents and trips below.");
        // Set empty summary structure so page can still render
        setSummary({
          agencyHealth: {},
          attentionRequired: { totalCount: 0, items: [] },
          agentActivity: { totalAgents: 0, agents: [] },
          clientPipeline: {},
          systemStatus: {}
        });
      } else {
        setError(err.message || 'Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Format time ago - returns null-safe string
  const formatTimeAgo = (dateString) => {
    // Handle null/undefined dates
    if (!dateString) {
      return '—';
    }

    try {
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return '—';
      }

      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) {
        return 'Just now';
      } else if (diffMins < 60) {
        return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
      } else if (diffHours < 24) {
        return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
      } else {
        return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
      }
    } catch (error) {
      // If any error occurs during date parsing/formatting, return dash
      console.warn('Error formatting date:', error);
      return '—';
    }
  };

  // Get attention item icon
  const getAttentionIcon = (type) => {
    switch (type) {
      case 'TRIP_STALLED':
        return AlertCircle;
      case 'FORM_ABANDONED':
        return FileText;
      case 'NO_ITINERARY':
        return Calendar;
      default:
        return AlertCircle;
    }
  };

  // Get attention item color classes
  const getAttentionColorClasses = (type) => {
    switch (type) {
      case 'TRIP_STALLED':
        return {
          bg: 'bg-red-100',
          text: 'text-red-600',
        };
      case 'FORM_ABANDONED':
        return {
          bg: 'bg-orange-100',
          text: 'text-orange-600',
        };
      case 'NO_ITINERARY':
        return {
          bg: 'bg-blue-100',
          text: 'text-blue-600',
        };
      default:
        return {
          bg: 'bg-gray-100',
          text: 'text-gray-600',
        };
    }
  };

  if (!hasAgencyAdminAccess) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-4 w-16 h-16 text-red-500" />
          <h2 className="mb-2 text-2xl font-bold text-gray-900">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access the Admin dashboard.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="mx-auto mb-4 admin-spinner"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !summary) {
    // Only show error screen if we don't have summary data
    // If we have summary but error (from 404), show the summary anyway
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="p-8 max-w-md text-center bg-white rounded-lg shadow-sm">
          <AlertCircle className="mx-auto mb-4 w-16 h-16 text-red-500" />
          <h2 className="mb-2 text-2xl font-bold text-gray-900">Error Loading Dashboard</h2>
          <p className="mb-6 text-gray-600">{error}</p>
          <button 
            onClick={() => fetchDashboardSummary()}
            className="px-4 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-4 w-16 h-16 text-gray-400" />
          <h2 className="mb-2 text-2xl font-bold text-gray-900">No Data Available</h2>
          <p className="text-gray-600">Dashboard data is not available at this time.</p>
        </div>
      </div>
    );
  }

  const { agencyHealth, attentionRequired, agentActivity, clientPipeline, systemStatus, ownerKpis } = summary;

  // Agency Health Stat Cards
  const healthStats = [
    {
      title: 'Active Agents',
      value: agencyHealth?.activeAgentsCount || 0,
      icon: Users,
      color: 'blue',
      subtitle: `${agencyHealth?.activeAgentsCount || 0} currently active`,
    },
    {
      title: 'Active Trips',
      value: agencyHealth?.activeTripsCount || 0,
      icon: Calendar,
      color: 'green',
      subtitle: 'In progress',
    },
    {
      title: 'Itineraries (30d)',
      value: agencyHealth?.itinerariesLast30Days || 0,
      icon: FileText,
      color: 'purple',
      subtitle: 'Last 30 days',
    },
    {
      title: 'Pending Forms',
      value: agencyHealth?.pendingClientFormsCount || 0,
      icon: Clock,
      color: agencyHealth?.pendingClientFormsCount > 0 ? 'orange' : 'gray',
      subtitle: 'Awaiting client response',
    },
  ];

  // Add stalled trips if available
  if (agencyHealth?.stalledTripsCount !== undefined && agencyHealth.stalledTripsCount > 0) {
    healthStats.push({
      title: 'Stalled Trips',
      value: agencyHealth.stalledTripsCount,
      icon: AlertTriangle,
      color: 'red',
      subtitle: 'Requires attention',
    });
  }

  const headerActions = [
    <button
      key="refresh"
      onClick={() => fetchDashboardSummary(true)}
      disabled={refreshing}
      className="flex items-center px-3 py-2 text-sm text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50 disabled:opacity-50"
    >
      <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
      Refresh
    </button>,
  ];

  return (
    <>
      <AdminPageHeader
        title="Agency Command Center"
        subtitle="Adoption, production, and what needs your attention today"
        actions={headerActions}
      />

      {/* Attention Required Banner */}
      {attentionRequired && attentionRequired.totalCount > 0 && (
        <AdminPanel
          variant="bordered"
          className="mb-6 bg-orange-50 border-orange-200"
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <AlertCircle className="mr-3 w-5 h-5 text-orange-600" />
              <div>
                <h3 className="text-sm font-semibold text-orange-900">
                  {attentionRequired.totalCount} item{attentionRequired.totalCount !== 1 ? 's' : ''} require attention
                </h3>
                <p className="text-xs text-orange-700">Review and take action below</p>
              </div>
            </div>
            <button
              onClick={() => {
                // Navigate to attention inbox
                navigate('/agent/admin/attention');
              }}
              className="flex items-center px-3 py-1.5 text-sm font-medium text-orange-700 bg-orange-100 rounded-lg transition-colors hover:bg-orange-200"
            >
              View All
              <ArrowRight className="ml-1 w-4 h-4" />
            </button>
          </div>
        </AdminPanel>
      )}

      {/* Agency Health Stats */}
      <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 lg:grid-cols-4">
        {healthStats.map((stat) => {
          // Determine navigation path based on stat type
          let navigationPath = null;
          if (stat.title === 'Active Agents') {
            navigationPath = '/agent/admin/agents';
          } else if (stat.title === 'Active Trips') {
            navigationPath = '/agent/admin/trips?filter=active';
          } else if (stat.title === 'Itineraries (30d)') {
            navigationPath = '/agent/admin/reports?view=itineraries';
          } else if (stat.title === 'Pending Forms') {
            navigationPath = '/agent/admin/trips?filter=pendingForms';
          } else if (stat.title === 'Stalled Trips') {
            navigationPath = '/agent/admin/trips?filter=stalled';
          }

          return (
            <div
              key={stat.title}
              onClick={() => navigationPath && navigate(navigationPath)}
              className={navigationPath ? 'transition-transform cursor-pointer hover:scale-105' : ''}
            >
              <AdminStatCard
                title={stat.title}
                value={stat.value.toLocaleString()}
                subtitle={stat.subtitle}
                icon={stat.icon}
                color={stat.color}
              />
            </div>
          );
        })}
      </div>

      {/* This Month KPIs (Owner-specific) */}
      {ownerKpis && (
        <AdminPanel
          title="This Month"
          subtitle="Month-to-date metrics"
          className="mb-8"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center mb-2">
                <FileText className="mr-2 w-4 h-4 text-blue-600" />
                <p className="text-sm font-medium text-blue-900">Itineraries Generated</p>
              </div>
              <p className="text-2xl font-bold text-blue-900">{ownerKpis.itinerariesGeneratedMTD || 0}</p>
              <p className="mt-1 text-xs text-blue-700">MTD</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center mb-2">
                <Calendar className="mr-2 w-4 h-4 text-green-600" />
                <p className="text-sm font-medium text-green-900">Trips Created</p>
              </div>
              <p className="text-2xl font-bold text-green-900">{ownerKpis.tripsCreatedMTD || 0}</p>
              <p className="mt-1 text-xs text-green-700">MTD</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center mb-2">
                <Users className="mr-2 w-4 h-4 text-purple-600" />
                <p className="text-sm font-medium text-purple-900">Active Agents (7d)</p>
              </div>
              <p className="text-2xl font-bold text-purple-900">{ownerKpis.activeAgentsLast7Days || 0}</p>
              <p className="mt-1 text-xs text-purple-700">Last 7 days</p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center mb-2">
                <Users className="mr-2 w-4 h-4 text-orange-600" />
                <p className="text-sm font-medium text-orange-900">Seat Utilization</p>
              </div>
              <p className="text-2xl font-bold text-orange-900">{ownerKpis.seatUtilizationPercent ? `${ownerKpis.seatUtilizationPercent.toFixed(0)}%` : '—'}</p>
              <p className="mt-1 text-xs text-orange-700">Capacity usage</p>
            </div>
          </div>
        </AdminPanel>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Attention Required */}
        {attentionRequired && (
          <AdminPanel
            id="attention-required"
            title="Attention Required"
            subtitle={`${attentionRequired.totalCount} item${attentionRequired.totalCount !== 1 ? 's' : ''} need action`}
            headerActions={[
              attentionRequired.totalCount > 0 && (
                <button
                  key="view-all"
                  onClick={() => {
                    // Navigate to attention inbox
                    navigate('/agent/admin/attention');
                  }}
                  className="text-sm font-medium text-blue-600 hover:text-blue-800"
                >
                  View All
                </button>
              ),
            ].filter(Boolean)}
            className={attentionRequired.totalCount > 0 ? 'lg:col-span-2' : 'lg:col-span-3'}
          >
            {attentionRequired.items && attentionRequired.items.length > 0 ? (
              <div className="space-y-3">
                {attentionRequired.items.map((item) => {
                  const Icon = getAttentionIcon(item.type);
                  const colorClasses = getAttentionColorClasses(item.type);
                  return (
                    <div
                      key={item.id}
                      onClick={() => navigate(item.targetUrl)}
                      className="flex items-center p-3 space-x-3 rounded-lg border border-gray-200 transition-colors cursor-pointer hover:bg-gray-50"
                    >
                      <div className={`flex-shrink-0 p-2 ${colorClasses.bg} rounded-lg`}>
                        <Icon className={`w-5 h-5 ${colorClasses.text}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{item.message}</p>
                        {item.count !== undefined && (
                          <p className="text-xs text-gray-500">{item.count} item{item.count !== 1 ? 's' : ''}</p>
                        )}
                      </div>
                      <ArrowRight className="flex-shrink-0 w-5 h-5 text-gray-400" />
                    </div>
                  );
                })}
              </div>
            ) : (
              <AdminEmptyState
                icon={CheckCircle}
                title="All clear"
                description="No items require attention at this time."
              />
            )}
          </AdminPanel>
        )}

        {/* Agent Activity */}
        {agentActivity && (
          <AdminPanel
            title="Agent Activity"
            subtitle={`${agentActivity.totalAgents} total agent${agentActivity.totalAgents !== 1 ? 's' : ''}`}
            headerActions={[
              <button
                key="view-all"
                onClick={() => navigate('/agent/admin/agents')}
                className="text-sm font-medium text-blue-600 hover:text-blue-800"
              >
                View All
              </button>,
            ]}
          >
            {agentActivity.agents && agentActivity.agents.length > 0 ? (
              <div className="space-y-3">
                {agentActivity.agents.slice(0, 5).map((agent) => (
                  <div
                    key={agent.agentId}
                    onClick={() => navigate(`/agent/admin/agents/${agent.agentId}`)}
                    className="flex justify-between items-center p-3 rounded-lg border border-gray-200 transition-colors cursor-pointer hover:bg-gray-50"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900 truncate">{agent.name}</p>
                        {agent.status === 'ACTIVE' && (
                          <div className="ml-2 w-2 h-2 bg-green-500 rounded-full"></div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {agent.activeTripsCount} active trip{agent.activeTripsCount !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <div className="flex-shrink-0 ml-3 text-right">
                      <p className="text-xs text-gray-500">
                        {formatTimeAgo(agent.lastActivityAt)}
                      </p>
                      {agent.status === 'LOW_ACTIVITY' && (
                        <p className="text-xs text-orange-600">Low activity</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <AdminEmptyState
                icon={Users}
                title="No agents"
                description="Agent activity will appear here once agents are added."
              />
            )}
          </AdminPanel>
        )}

        {/* Client Pipeline (Optional) */}
        {clientPipeline && (
          <AdminPanel
            title="Client Pipeline"
            subtitle="Form completion metrics"
            className="lg:col-span-2"
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center mb-2">
                  <FileText className="mr-2 w-4 h-4 text-blue-600" />
                  <p className="text-sm font-medium text-blue-900">Forms Sent</p>
                </div>
                <p className="text-2xl font-bold text-blue-900">{clientPipeline.formsSentCount}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center mb-2">
                  <CheckCircle className="mr-2 w-4 h-4 text-green-600" />
                  <p className="text-sm font-medium text-green-900">Completed</p>
                </div>
                <p className="text-2xl font-bold text-green-900">{clientPipeline.formsCompletedCount}</p>
                {clientPipeline.formsSentCount > 0 && (
                  <p className="mt-1 text-xs text-green-700">
                    {Math.round((clientPipeline.formsCompletedCount / clientPipeline.formsSentCount) * 100)}% completion rate
                  </p>
                )}
              </div>
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center mb-2">
                  <AlertCircle className="mr-2 w-4 h-4 text-orange-600" />
                  <p className="text-sm font-medium text-orange-900">Abandoned</p>
                </div>
                <p className="text-2xl font-bold text-orange-900">{clientPipeline.abandonedFormsCount}</p>
              </div>
            </div>
            {clientPipeline.averageCompletionTimeHours && (
              <div className="p-3 mt-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center">
                  <Clock className="mr-2 w-4 h-4 text-gray-600" />
                  <p className="text-sm text-gray-700">
                    Average completion time: <span className="font-semibold">{clientPipeline.averageCompletionTimeHours.toFixed(1)} hours</span>
                  </p>
                </div>
              </div>
            )}
          </AdminPanel>
        )}

        {/* System Status (Optional) */}
        {systemStatus && (
          <AdminPanel
            title="System Status"
            subtitle="Subscription and health"
          >
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <p className="text-sm font-medium text-gray-700">Plan</p>
                  <p className="text-sm font-semibold text-gray-900">{systemStatus.planName}</p>
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-600">Agent Seats</p>
                  <p className="text-sm font-medium text-gray-900">
                    {systemStatus.agentSeatsUsed} / {systemStatus.agentSeatLimit}
                  </p>
                </div>
                <div className="mt-2 w-full h-2 bg-gray-200 rounded-full">
                  <div
                    className="h-2 bg-blue-600 rounded-full"
                    style={{
                      width: `${Math.min((systemStatus.agentSeatsUsed / systemStatus.agentSeatLimit) * 100, 100)}%`,
                    }}
                  ></div>
                </div>
              </div>
              <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                <div className="flex items-center">
                  <div className={`w-2 h-2 mr-2 rounded-full ${
                    systemStatus.systemHealth === 'OPERATIONAL' ? 'bg-green-500' : 'bg-orange-500'
                  }`}></div>
                  <p className="text-sm text-gray-700">System Health</p>
                </div>
                <p className={`text-sm font-medium ${
                  systemStatus.systemHealth === 'OPERATIONAL' ? 'text-green-600' : 'text-orange-600'
                }`}>
                  {systemStatus.systemHealth}
                </p>
              </div>
            </div>
          </AdminPanel>
        )}
      </div>
    </>
  );
};

export default AgencyDashboard;
